import React, { useCallback, ChangeEvent, useState, useEffect } from 'react'
import { VideoUploadButton } from '../UploadButtons'
import styled from 'styled-components'
// import { AxiosProgressEvent } from 'axios'
import { toast } from 'react-toastify'
import ClearButton from '../ClearButton'
import { UploaderErrorText, UploaderText, UploaderTitle } from '../styles'
import { Box } from '../../UI/Box'
import { theseus } from '../../../api/theseus'

export interface UploadableVideo {
  url: string
}

const Wrapper = styled(Box)`
  width: 60%;
`

function VideoUploadSelector({
  projectId = 0,
  fileUrl,
  setFileUrl,
  thumbnailWidth = 200,
  thumbnailHeight = 100,
  setIsUploading,
}: {
  projectId: number | undefined
  fileUrl: string
  setFileUrl: (url: string) => void
  thumbnailWidth?: number
  thumbnailHeight?: number
  setIsUploading?: (isLoading: boolean) => void
}) {
  const { handleVideoUpload, isUploading, progress, error } =
    useVideoUpload(projectId)

  const [isImage, setIsImage] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    const isImageUrl = async () => {
      if (!fileUrl) return
      setIsLoading(true)
      setIsUploading?.(true)
      return new Promise((resolve, reject) => {
        fetch(fileUrl)
          .then((response) => {
            const contentType = response.headers.get('content-type')
            const isTypeImage = contentType && contentType.startsWith('image')
            setIsImage(isTypeImage)
            setIsLoading(false)
            resolve(isTypeImage)
          })
          .catch((error) => {
            setIsLoading(false)
            setIsUploading?.(false)
            setIsImage(false)
            reject(error)
          })
      })
    }

    isImageUrl()
  }, [fileUrl])

  const onVideoUpload = useCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      const uploadedVideo = await handleVideoUpload(e)
      if (!uploadedVideo) return
      const { url } = uploadedVideo
      setFileUrl(url)
    },
    [handleVideoUpload, setFileUrl],
  )
  const clearVideo = useCallback(() => setFileUrl(undefined), [setFileUrl])

  useEffect(() => {
    setIsUploading?.(isUploading)
  }, [isUploading])

  return (
    <Wrapper dc>
      {fileUrl && !isLoading && !isUploading ? (
        <Box>
          {isImage ? (
            <img src={fileUrl} alt="uploaded image" width="400" />
          ) : (
            <video controls width="400">
              <source src={fileUrl} />
            </video>
          )}
          <ClearButton onClick={clearVideo} isVisible />
        </Box>
      ) : (
        <InputContainer>
          <VideoUploadCard>
            {error && <UploaderErrorText>{error}</UploaderErrorText>}
            <VideoUploadButton
              onVideoChange={onVideoUpload}
              isLoading={isUploading || isLoading}
              progress={progress}
              button={
                <VideoSelectButton
                  uploadedVideoUrl={fileUrl}
                  thumbnailWidth={thumbnailWidth}
                  thumbnailHeight={thumbnailHeight}
                />
              }
            />
          </VideoUploadCard>
        </InputContainer>
      )}
    </Wrapper>
  )
}

export default VideoUploadSelector

const InputContainer = styled.div`
  width: 100%;

  // for making the input of upload button absolute and cover the whole area
  position: relative;

  input {
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    opacity: 0;
    cursor: pointer;
    height: 100%;
  }
`

const VideoUploadCard = styled.div`
  border-radius: 8px;
  border: 2px solid ${({ theme }) => theme.colors.cardBorder};
  background: ${({ theme }) => theme.colors.cardBackground};

  :hover,
  :active,
  :active,
  :focus {
    border: 2px solid ${({ theme }) => theme.colors.brandPrimary};
  }

  cursor: pointer;
  display: flex;
  justify-content: center;
`

const StyledVideoUploadButton = styled.button<{
  hasVideo: boolean
  thumbnailWidth: number
  thumbnailHeight: number
}>`
  height: ${({ hasVideo, thumbnailHeight }) =>
    hasVideo ? 'auto' : `${thumbnailHeight}px`};
  border-radius: 8px;
  padding: 0;
  z-index: ${({ hasVideo }) => (hasVideo ? 1 : 0)};
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  align-self: center;
  cursor: pointer;
  outline: none;
  background: none;
  overflow: hidden;
  @media screen and (max-width: ${({ theme }) => theme.breakpoints.md}px) {
    max-width: 100%;
  }
`

function VideoSelectButton({
  uploadedVideoUrl,
  thumbnailWidth,
  thumbnailHeight,
}: {
  uploadedVideoUrl?: string
  thumbnailWidth: number
  thumbnailHeight: number
}) {
  return (
    <StyledVideoUploadButton
      hasVideo={!!uploadedVideoUrl}
      thumbnailWidth={thumbnailWidth}
      thumbnailHeight={thumbnailHeight}
    >
      {uploadedVideoUrl ? (
        <video controls width="400">
          <source src={uploadedVideoUrl} />
        </video>
      ) : (
        <Box g={2}>
          <img src={'/upload.svg'} alt="button icon" />
          Click to choose a file or drag here
        </Box>
      )}
    </StyledVideoUploadButton>
  )
}

export function useVideoUpload(projectId?: number) {
  const [isUploading, setIsUploading] = useState(false)
  const [progress, setProgress] = useState(0)
  const [error, setError] = useState('')

  const handleVideoUpload = useCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      try {
        if (!e.target.files) throw new Error('No files found')
        const file = e.target.files[0]
        if (!file) throw new Error('No file selected')

        setIsUploading(true)
        setError('')

        const formData = new FormData()
        formData.append('file', file)
        formData.append('name', file.name)

        const res = await theseus.post(`/forms/upload-file/generic`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        })

        const uploadedFileUrl = res.data?.uploadedFileUrl
        if (!uploadedFileUrl) throw new Error('No video returned from upload')
        return uploadedFileUrl
      } catch (err) {
        console.error(err)
        let message = 'Error uploading video'
        if (typeof err === 'string') {
          message = err
        } else {
          message =
            (err as { status: number }).status === 413
              ? 'Video size too big'
              : 'Error uploading video'
        }
        toast.error(message)
        setError(message)
      } finally {
        setIsUploading(false)
        setProgress(0)
      }
    },
    [projectId],
  )

  return { handleVideoUpload, isUploading, progress, error }
}
